<template>
  <div class="open-jobs">
    <div v-if="loading">
      <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
    </div>
    <div v-else-if="hasOpenJobs">
      <SeekerJobCard
        v-for="job in openJobs"
        :key="job.id"
        :job="job"
        :account="account"
        class="mb-16"
        @click="handleJobClick"
        @manage-job="onManageJob"
      />
      <Button
        v-if="hasNextOpenPage"
        variant="secondary"
        class="cursor-pointer w-full mb-64 sm:flex sm:w-auto sm:mx-auto"
        :loading="loadingMore"
        @click.prevent="loadMore"
      >
        View More
      </Button>
    </div>
    <div v-else class="open-jobs__empty">
      <h2 class="text-h4 w-full mb-8">Let's create a job</h2>
      <p class="text-body mb-16">Share how you need help and matching caregivers will reach out to you</p>
      <div class="mb-16 grid grid-cols-2 gap-16" :class="gridClasses">
        <CareTypeCard v-for="careType in careTypes" :key="careType" :careType="careType" @select="handleCareTypeClick" />
      </div>
    </div>
    <ActionsPopup v-if="currentJob" v-model="showManagePopup" title="Manage Job" :buttons="jobActions">
      <p v-if="userOwnsCurrentJob || currentJobApplicationCount > 0" class="text-body mb-16">{{ currentJob.title }}</p>
      <p v-else class="text-body mb-16">
        No applications yet. Family Concierge will notify you when interested caregivers have applied to your job. For help,
        please contact the Family Concierge team.
      </p>
    </ActionsPopup>
    <ActionsPopup v-if="currentJob" v-model="showCloseConfirmation" title="Close this job?" :buttons="closeConfirmationButtons">
      <p class="text-body">Once you close this job, new sitters will no longer be able to apply for it</p>
    </ActionsPopup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import ActionsPopup from '@/components/actionsPopup/ActionsPopup.vue';
import type { ActionButton } from '@/components/actionsPopup/types';
import SeekerJobCard from '@/components/seekerJobCard/SeekerJobCard.vue';
import Button from '@/components/button/Button.vue';
import CareTypeCard from '@/components/careType/CareTypeCard.vue';
import { redirect } from '@/helpers/redirects';
import {
  createViewApplicantsButton,
  createCloseConfirmationButtons,
  createActiveJobManagementButtons,
} from '@/helpers/jobActionButtons';
import { FETCH_OPEN_JOBS } from '@/graphql/account/queries';

export default defineComponent({
  name: 'SeekerJobsOpen',
  components: {
    ActionsPopup,
    SeekerJobCard,
    Button,
    CareTypeCard,
  },
  apollo: {
    viewer: {
      query: FETCH_OPEN_JOBS,
      result() {
        this.totalCount = this.viewer?.jobs?.totalCount ?? 0;
        this.lastPageInfo = this.viewer?.jobs?.pageInfo ?? null;
        this.openJobs = (this.viewer?.jobs?.edges ?? []).map((edge: any) => edge.node);
        this.loading = false;
      },
      error() {
        this.loading = false;
      },
    },
  },
  data(): {
    totalCount: number;
    openJobs: any[];
    loading: boolean;
    loadingMore: boolean;
    currentJob: any;
    showManagePopup: boolean;
    showCloseConfirmation: boolean;
    lastPageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
  } {
    return {
      totalCount: 0,
      openJobs: [],
      loading: true,
      loadingMore: false,
      currentJob: null,
      showManagePopup: false,
      showCloseConfirmation: false,
      lastPageInfo: null,
    };
  },
  computed: {
    ...mapGetters('account', { account: 'getAccount' }),
    currentJobApplicationCount(): number {
      return this.currentJob?.applications?.totalCount ?? 0;
    },
    userOwnsCurrentJob(): boolean {
      const job = this.currentJob;
      return !!(job && this.account?.id === job.parent?.id);
    },
    closeConfirmationButtons(): ActionButton[] {
      return createCloseConfirmationButtons({
        onConfirm: () => this.closeJobMutation(),
        onCancel: () => this.cancelCloseConfirmation(),
      });
    },
    jobActions(): ActionButton[] {
      const job = this.currentJob;
      if (!job) return [];

      let buttons: ActionButton[] = [];

      if (this.currentJobApplicationCount > 0) {
        buttons.push(createViewApplicantsButton(job.id));
      }

      // the list of jobs returned for an account includes jobs that have been shared with the account
      // this checks that the user is the manager of the job
      if (this.userOwnsCurrentJob) {
        buttons = buttons.concat(
          createActiveJobManagementButtons({
            jobId: job.id,
            onCloseJob: () => this.openCloseConfirmation(),
            isFamilyConcierge: this.account?.isFamilyConcierge(),
          })
        );
      }

      if (buttons.length > 0) {
        buttons[0].variant = 'primary';
      }

      return buttons;
    },
    hasOpenJobs(): boolean {
      return this.totalCount > 0;
    },
    hasNextOpenPage(): boolean {
      if (this.lastPageInfo) return this.lastPageInfo.hasNextPage;
      return false;
    },
    corporate(): boolean {
      return this.account.isCorporate();
    },
    careTypes(): Array<string> {
      const careTypes = ['CHILD_CARE', 'COMPANION_CARE', 'PET_CARE', 'SPECIAL_NEEDS'];

      if (this.corporate) {
        careTypes.push('HOUSEKEEPING');
      }

      return careTypes;
    },
    gridClasses(): string {
      return this.corporate ? 'md:grid-cols-3 md-2:grid-cols-5' : 'md:grid-cols-4';
    },
  },
  methods: {
    onManageJob(job: any) {
      this.currentJob = job;
      this.showManagePopup = true;
    },
    openCloseConfirmation() {
      this.showManagePopup = false;
      this.showCloseConfirmation = true;
    },
    cancelCloseConfirmation() {
      this.showCloseConfirmation = false;
      this.showManagePopup = true;
    },
    async closeJobMutation() {
      this.showCloseConfirmation = false;
      if (!this.currentJob) return;
      const jobId = this.currentJob.id;
      try {
        await this.$store.dispatch('job/closeJob', jobId);
        redirect(`/jobs/action_with_uuid?job_action=job_closed_questionnaire&uuid=${jobId}`);
      } catch {
        // Error already logged in store action
      }
    },
    handleJobClick(card: any, job: any, event?: Event) {
      if (event) event.preventDefault();
      if (!job) return;

      const hasApplications = card.totalNewApplicants > 0;
      if (hasApplications) {
        this.$router.push({ name: 'applications', params: { job_uuid: job.id } });
        return;
      }

      this.currentJob = job;
      this.showManagePopup = true;
    },
    getLastCursor(): string | null {
      if (this.lastPageInfo?.endCursor) return this.lastPageInfo.endCursor;
      return null;
    },
    async loadMore() {
      const after = this.getLastCursor();
      if (!after || this.loadingMore) return;

      this.loadingMore = true;

      try {
        // Separate query (not the smart query's fetchMore) so the viewer query is never re-run.
        const { data } = await this.$apollo.query({
          query: FETCH_OPEN_JOBS,
          variables: { after },
          fetchPolicy: 'network-only',
        });
        const jobs = data?.viewer?.jobs;
        this.lastPageInfo = jobs?.pageInfo ?? null;
        const newNodes = (jobs?.edges ?? []).map((edge: any) => edge.node);
        this.openJobs = [...this.openJobs, ...newNodes];
      } catch (error) {
        console.error('Error loading more open jobs:', error);
      } finally {
        this.loadingMore = false;
      }
    },
    handleCareTypeClick(careType: string) {
      if (careType === 'CHILD_CARE') {
        // Use Vue Router for child care navigation
        this.$router.push('/job-post');
      } else {
        // Convert care type to lowercase for URL parameter (e.g., PET_CARE -> pet_care)
        const careTypeParam = careType.toLowerCase();
        // Do HTTP redirect for other care types
        window.location.href = `/job_posting/new?care_type=${careTypeParam}`;
      }
    },
  },
});
</script>
